<template>
  <div class="min-max-slider" ref="elMinMaxSlider">
    <label for="min">Minimum price</label>
    <input
      id="min"
      class="min"
      name="min"
      type="range"
      :step="`${step}`"
      ref="elMin"
    />
    <label for="max">Maximum price</label>
    <input
      id="max"
      class="max"
      name="max"
      type="range"
      :step="`${step}`"
      ref="elMax"
    />
  </div>
</template>
<script>
/**
 * Displays a widget with two input range as if it was a single one with min and max values
 *
 * @implements
 * CommMixin
 *
 * @props
 * thumbsize: Number
 * rangeWidth: Number
 * step: Number
 * rangeMin: Number
 * rangeMax: Number
 * isCurrency: Boolean
 * type: String
 * round: Boolean
 * reducer: Number
 *
 * @emits
 * rb-update-filters
 */
import CommMixin from "@/mixins/CommMixin";
export default {
  mixins: [CommMixin],
  props: {
    thumbsize: {
      type: Number,
      default: 14,
    },
    rangeWidth: {
      type: Number,
      default: 300,
    },
    step: {
      type: Number,
      default: 1,
    },
    rangeMin: {
      type: Number,
      default: 0,
    },
    rangeMax: {
      type: Number,
      default: 100,
    },
    isCurrency: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "",
    },
    round: {
      type: Boolean,
      default: false,
    },
    reducer: {
      type: Number,
      default: null,
    },
  },
  data: function () {
    return {
      elMinMaxSlider: null,
      elMin: null,
      elMax: null,
      legendNum: 2,
      legendvalues: [],
    };
  },
  mounted: function () {
    this.elMinMaxSlider = this.$refs.elMinMaxSlider;
    this.elMin = this.$refs.elMin;
    this.elMax = this.$refs.elMax;

    this.init();
  },
  methods: {
    init: function () {
      this.averageValue = (this.rangeMin + this.rangeMax) / 2;

      this.elMin.setAttribute("min", this.rangeMin);
      this.elMin.setAttribute("max", this.rangeMax);
      this.elMax.setAttribute("min", this.rangeMin);
      this.elMax.setAttribute("max", this.rangeMax);

      /* set data-values */
      this.elMin.setAttribute("data-value", this.rangeMin);
      this.elMax.setAttribute("data-value", this.rangeMax);

      this.elMinMaxSlider.style.width = `${this.rangeWidth}px`;

      this.writeLegends();

      /* draw */
      this.draw(this.averageValue);

      /* events */
      this.elMin.addEventListener("input", () => {
        this.update(this.elMin);
      });
      this.elMax.addEventListener("input", () => {
        this.update(this.elMax);
      });
    },
    writeLegends: function () {
      /* write legend */
      var legend = document.createElement("div");
      legend.classList.add("legend");
      this.legendvalues = [];
      for (var i = 0; i < this.legendNum; i++) {
        this.legendvalues[i] = document.createElement("div");
        var val = Math.round(
          this.rangeMin +
            (i / (this.legendNum - 1)) * (this.rangeMax - this.rangeMin)
        );
        var valDisplay = this.reducer
          ? parseFloat(val / this.reducer).toFixed(2)
          : val;
        this.legendvalues[i].appendChild(
          document.createTextNode(
            this.isCurrency ? `$${this.moneyFormat(valDisplay)}` : valDisplay
          )
        );
        legend.appendChild(this.legendvalues[i]);
      }
      this.elMinMaxSlider.appendChild(legend);
    },
    draw: function (splitvalue) {
      var legend = this.elMinMaxSlider.querySelector(".legend");

      /* set min and max attributes */
      this.elMin.setAttribute("max", splitvalue);
      this.elMax.setAttribute("min", splitvalue);

      //   console.log(splitvalue);

      /* set css */
      this.elMin.style.width = `${parseInt(
        this.thumbsize +
          ((splitvalue - this.rangeMin) / (this.rangeMax - this.rangeMin)) *
            (this.rangeWidth - 2 * this.thumbsize)
      )}px`;

      this.elMax.style.width = `${parseInt(
        this.thumbsize +
          ((this.rangeMax - splitvalue) / (this.rangeMax - this.rangeMin)) *
            (this.rangeWidth - 2 * this.thumbsize)
      )}px`;

      this.elMin.style.left = 0;
      this.elMax.style.left = `${parseInt(this.elMin.style.width)}px`;
      this.elMin.style.top = 0;
      this.elMax.style.top = 0;
      legend.style.position = "relative";
      legend.style.top = "20px";
      this.elMinMaxSlider.style.height = 20 + 20 + "px";

      /* correct for 1 off at the end */
      if (this.elMax.value > this.rangeMax - 1)
        this.elMax.setAttribute("data-value", this.rangeMax);

      /* write value and labels */
      this.elMax.value = this.elMax.getAttribute("data-value");
      this.elMin.value = this.elMin.getAttribute("data-value");
    },
    update: function () {
      //   var slider = el.parentElement;
      //   console.log("slider", slider);
      var min = this.elMin;
      var max = this.elMax;
      var minvalue = Math.floor(min.value);
      var maxvalue = Math.floor(max.value);

      /* set inactive values before draw */
      min.setAttribute("data-value", minvalue);
      max.setAttribute("data-value", maxvalue);

      var avgvalue = (minvalue + maxvalue) / 2;

      /* draw */
      this.draw(avgvalue);

      /** update mins and maxs displays */
      var minDisplayValue = this.reducer
        ? parseFloat(min.dataset.value / this.reducer).toFixed(2)
        : min.dataset.value;
      var maxDisplayValue = this.reducer
        ? parseFloat(max.dataset.value / this.reducer).toFixed(2)
        : max.dataset.value;
      this.legendvalues[0].innerHTML = this.isCurrency
        ? `$${this.moneyFormat(minDisplayValue)}`
        : minDisplayValue;
      this.legendvalues[1].innerHTML = this.isCurrency
        ? `$${this.moneyFormat(maxDisplayValue)}`
        : maxDisplayValue;

      var minBroadcastValue = this.reducer
        ? parseFloat(min.dataset.value / this.reducer).toFixed(2)
        : min.dataset.value;
      var maxBroadcastValue = this.reducer
        ? parseFloat(max.dataset.value / this.reducer).toFixed(2)
        : max.dataset.value;
      this.broadcast("rb-update-filters", {
        id: 1,
        min: parseFloat(minBroadcastValue),
        max: parseFloat(maxBroadcastValue),
        title: "Price",
        value: "",
        type: this.type,
      });
    },
    clear: function () {
      this.elMin.dataset.value = this.rangeMin;
      this.elMin.value = this.rangeMin;
      this.elMax.dataset.value = this.rangeMax;
      this.elMax.value = this.rangeMax;
      this.update(this.elMin);
      this.update(this.elMax);
    },
    moneyFormat: function (val) {
      var currencyFormatter = new Intl.NumberFormat({
        style: "currency",
        currency: "USD",
      });
      return currencyFormatter.format(parseFloat(val));
    },
  },
};
</script>
